import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Save, User, Users, ClipboardList,
  Briefcase, FileCheck, Plus, Trash2, CheckCircle, Send
} from 'lucide-react';
import axios from 'axios';
import FieldRow from '../components/FieldRow';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: "Personal Details", icon: User },
  { id: 2, title: "Family Details", icon: Users },
  { id: 3, title: "Assessment & Counselling", icon: ClipboardList },
  { id: 4, title: "Micro Business Plan", icon: Briefcase },
  { id: 5, title: "Declaration & Submit", icon: FileCheck },
];

export default function PublicSHNDPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState('');

  const [personalDetails, setPersonalDetails] = useState({
    name: '', its: '', age: '', mauze_jamiat: '', contact_number: '',
    email: '', residential_address: '', present_occupation: '',
    occupation_address: '', other_info: ''
  });

  const [familyDetails, setFamilyDetails] = useState({
    family_members: [{ name: '', age_relation: '', education_occupation: '', annual_income: '' }],
    family_structure: '', other_details: '',
    wellbeing: { housing: '', education: '', health: '', deeni: '', ziyarat: '' },
    income_expense: {
      income_business_m: '', income_business_y: '', income_salary_m: '', income_salary_y: '',
      income_home_industry_m: '', income_home_industry_y: '', income_others_m: '', income_others_y: '',
      total_income_monthly: '', total_income_yearly: '',
      exp_food_m: '', exp_food_y: '', exp_housing_m: '', exp_housing_y: '',
      exp_health_m: '', exp_health_y: '', exp_transport_m: '', exp_transport_y: '',
      exp_education_m: '', exp_education_y: '', exp_deeni_m: '', exp_deeni_y: '',
      exp_essentials_m: '', exp_essentials_y: '', exp_nonessentials_m: '', exp_nonessentials_y: '',
      exp_others_m: '', exp_others_y: '', total_expense_monthly: '', total_expense_yearly: '',
      surplus_monthly: '', surplus_yearly: '', borrowing_m: '', borrowing_y: ''
    },
    assets_liabilities: {
      asset_residential: '', asset_shop: '', asset_machinery: '', asset_stock: '',
      asset_goods_credit: '', asset_others: '',
      liability_borrowing: '', liability_goods_credit: '', liability_others: ''
    }
  });

  const [assessment, setAssessment] = useState({
    education: '', past_experience: '', present_business: '', family_business: '',
    skills: '', counsellor_assessment: '',
    products: [{ name: '', unit: '', cost_price: '' }],
    trademark: '', online_presence: '', digital_marketing: '', store_location: '',
    supplier_details: '', customer_details: '', major_expenses: '', business_assessment: '',
    support_areas: {}
  });

  const [businessPlan, setBusinessPlan] = useState({
    financial_assistance: [{ timeline: '', purpose: '', amount: '', support_doc: '' }],
    self_funding: '', rahen_available: '',
    repayment_schedule: {},
    economic_growth: {}
  });

  const [declaration, setDeclaration] = useState({
    applicant_name: '', counsellor_name: '', tr_member_name: '', comments: ''
  });

  const handleSubmit = async () => {
    if (!personalDetails.name.trim()) {
      toast.error('Please enter applicant name');
      return;
    }
    // Validate key fields before submit
    const errs = [];
    if (!personalDetails.contact_number || personalDetails.contact_number.replace(/\D/g, '').length < 10) {
      errs.push('Valid contact number is required');
    }
    if (personalDetails.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalDetails.email.trim())) {
      errs.push('Invalid email address');
    }
    if (personalDetails.its && !/^\d{8}$/.test(personalDetails.its)) {
      errs.push('ITS must be exactly 8 digits');
    }
    if (personalDetails.age) {
      const a = Number(personalDetails.age);
      if (!Number.isFinite(a) || a < 0 || a > 120) errs.push('Age must be between 0 and 120');
    }
    if (errs.length) {
      toast.error(errs[0]);
      setCurrentStep(1);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/api/public/shnd/submit`, {
        personal_details: personalDetails,
        family_details: familyDetails,
        assessment: assessment,
        business_plan: businessPlan,
        declaration: declaration,
      });
      setSubmittedId(res.data.id);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // Helpers
  const updatePD = (key, val) => setPersonalDetails(p => ({ ...p, [key]: val }));
  const updateWellbeing = (key, val) => setFamilyDetails(p => ({ ...p, wellbeing: { ...p.wellbeing, [key]: val } }));
  const updateIE = (key, val) => setFamilyDetails(p => ({ ...p, income_expense: { ...p.income_expense, [key]: val } }));
  const updateAL = (key, val) => setFamilyDetails(p => ({ ...p, assets_liabilities: { ...p.assets_liabilities, [key]: val } }));
  const updateAssess = (key, val) => setAssessment(p => ({ ...p, [key]: val }));
  const updateBP = (key, val) => setBusinessPlan(p => ({ ...p, [key]: val }));
  const updateRepay = (key, val) => setBusinessPlan(p => ({ ...p, repayment_schedule: { ...p.repayment_schedule, [key]: val } }));
  const updateGrowth = (key, val) => setBusinessPlan(p => ({ ...p, economic_growth: { ...p.economic_growth, [key]: val } }));
  const updateDecl = (key, val) => setDeclaration(p => ({ ...p, [key]: val }));
  const toggleSupport = (key) => setAssessment(p => ({ ...p, support_areas: { ...p.support_areas, [key]: !p.support_areas[key] } }));

  const addFamilyMember = () => setFamilyDetails(p => ({ ...p, family_members: [...p.family_members, { name: '', age_relation: '', education_occupation: '', annual_income: '' }] }));
  const removeFamilyMember = (i) => setFamilyDetails(p => ({ ...p, family_members: p.family_members.filter((_, idx) => idx !== i) }));
  const updateFamilyMember = (i, key, val) => setFamilyDetails(p => ({ ...p, family_members: p.family_members.map((m, idx) => idx === i ? { ...m, [key]: val } : m) }));
  const addProduct = () => setAssessment(p => ({ ...p, products: [...p.products, { name: '', unit: '', cost_price: '' }] }));
  const removeProduct = (i) => setAssessment(p => ({ ...p, products: p.products.filter((_, idx) => idx !== i) }));
  const updateProduct = (i, key, val) => setAssessment(p => ({ ...p, products: p.products.map((pr, idx) => idx === i ? { ...pr, [key]: val } : pr) }));
  const addFA = () => setBusinessPlan(p => ({ ...p, financial_assistance: [...p.financial_assistance, { timeline: '', purpose: '', amount: '', support_doc: '' }] }));
  const removeFA = (i) => setBusinessPlan(p => ({ ...p, financial_assistance: p.financial_assistance.filter((_, idx) => idx !== i) }));
  const updateFA = (i, key, val) => setBusinessPlan(p => ({ ...p, financial_assistance: p.financial_assistance.map((fa, idx) => idx === i ? { ...fa, [key]: val } : fa) }));

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Application Submitted</h2>
            <p className="text-sm text-slate-500">Your SHND application has been submitted successfully.</p>
            <p className="text-xs text-slate-400 font-mono bg-slate-50 p-2 rounded">Reference: {submittedId}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-6 px-4" data-testid="public-shnd-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-slate-900">SHND Application Form</h1>
          <p className="text-sm text-slate-500">Application and Assessment Form - Program: SHND</p>
        </div>

        {/* Step indicators */}
        <div className="flex gap-1 items-center justify-center overflow-x-auto pb-2">
          {STEPS.map((step) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isDone = currentStep > step.id;
            return (
              <button
                key={step.id}
                onClick={() => setCurrentStep(step.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  isActive ? 'bg-slate-900 text-white shadow-lg' :
                  isDone ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                  'bg-white text-slate-400 border border-slate-200'
                }`}
                data-testid={`pub-step-${step.id}`}
              >
                {isDone ? <CheckCircle className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{step.title}</span>
                <span className="sm:hidden">{step.id}</span>
              </button>
            );
          })}
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">{STEPS[currentStep - 1]?.title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* STEP 1 */}
            {currentStep === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldRow label="Full Name" required value={personalDetails.name} onChange={v => updatePD('name', v)} placeholder="Applicant's full name" testId="shnd-public-name" />
                <FieldRow label="ITS Number" value={personalDetails.its} onChange={v => updatePD('its', v)} placeholder="8-digit ITS" validation="its" testId="shnd-public-its" />
                <FieldRow label="Age" value={personalDetails.age} onChange={v => updatePD('age', v)} placeholder="Age" validation="age" testId="shnd-public-age" />
                <FieldRow label="Mauze & Jamiat" value={personalDetails.mauze_jamiat} onChange={v => updatePD('mauze_jamiat', v)} placeholder="Mauze and Jamiat" testId="shnd-public-mauze" />
                <FieldRow label="Contact Number" required value={personalDetails.contact_number} onChange={v => updatePD('contact_number', v)} placeholder="10-digit mobile" validation="mobile" testId="shnd-public-contact" />
                <FieldRow label="Email ID" value={personalDetails.email} onChange={v => updatePD('email', v)} placeholder="name@example.com" validation="email" testId="shnd-public-email" />
                <div className="md:col-span-2"><FieldRow label="Residential Address" value={personalDetails.residential_address} onChange={v => updatePD('residential_address', v)} placeholder="Full address" type="textarea" testId="shnd-public-address" /></div>
                <FieldRow label="Present Occupation" value={personalDetails.present_occupation} onChange={v => updatePD('present_occupation', v)} placeholder="Current occupation" testId="shnd-public-occupation" />
                <FieldRow label="Occupation Address" value={personalDetails.occupation_address} onChange={v => updatePD('occupation_address', v)} placeholder="Workplace address" testId="shnd-public-occ-address" />
                <div className="md:col-span-2"><FieldRow label="Other Relevant Information" value={personalDetails.other_info} onChange={v => updatePD('other_info', v)} type="textarea" testId="shnd-public-other-info" /></div>
              </div>
            )}

            {/* STEP 2 */}
            {currentStep === 2 && (
              <div className="space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-800">Family Members</h3>
                    <Button variant="outline" size="sm" onClick={addFamilyMember}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                  </div>
                  {familyDetails.family_members.map((m, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 bg-slate-50 rounded-lg mb-2">
                      <div className="col-span-12 sm:col-span-3"><Label className="text-xs text-slate-500">Name</Label><Input value={m.name} onChange={e => updateFamilyMember(i, 'name', e.target.value)} className="text-sm" /></div>
                      <div className="col-span-6 sm:col-span-3"><Label className="text-xs text-slate-500">Age & Relation</Label><Input value={m.age_relation} onChange={e => updateFamilyMember(i, 'age_relation', e.target.value)} className="text-sm" /></div>
                      <div className="col-span-6 sm:col-span-3"><Label className="text-xs text-slate-500">Education/Occupation</Label><Input value={m.education_occupation} onChange={e => updateFamilyMember(i, 'education_occupation', e.target.value)} className="text-sm" /></div>
                      <div className="col-span-10 sm:col-span-2"><Label className="text-xs text-slate-500">Annual Income</Label><Input value={m.annual_income} onChange={e => updateFamilyMember(i, 'annual_income', e.target.value)} className="text-sm" /></div>
                      <div className="col-span-2 sm:col-span-1 flex justify-end">{familyDetails.family_members.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeFamilyMember(i)} className="text-red-500 h-9 w-9 p-0"><Trash2 className="w-4 h-4" /></Button>}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldRow label="Family Structure" value={familyDetails.family_structure} onChange={v => setFamilyDetails(p => ({ ...p, family_structure: v }))} placeholder="Nuclear/Joint" />
                  <FieldRow label="Other Details" value={familyDetails.other_details} onChange={v => setFamilyDetails(p => ({ ...p, other_details: v }))} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Wellbeing / Lifestyle</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[['Housing', 'housing'], ['Education', 'education'], ['Health', 'health'], ['Deeni', 'deeni'], ['Ziyarat / Travel', 'ziyarat']].map(([l, k]) => (
                      <FieldRow key={k} label={l} value={familyDetails.wellbeing[k]} onChange={v => updateWellbeing(k, v)} />
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Income and Expense</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-100"><th className="p-2 text-left font-medium">Category</th><th className="p-2 text-left font-medium w-36">Monthly</th><th className="p-2 text-left font-medium w-36">Yearly</th></tr></thead>
                      <tbody>
                        <tr><td colSpan="3" className="p-2 font-semibold bg-blue-50">INCOME</td></tr>
                        {[['Business', 'income_business'], ['Salary', 'income_salary'], ['Home Industry', 'income_home_industry'], ['Others', 'income_others']].map(([l, k]) => (
                          <tr key={k} className="border-b border-slate-100"><td className="p-2 text-slate-600">{l}</td><td className="p-1"><Input value={familyDetails.income_expense[k+'_m']||''} onChange={e=>updateIE(k+'_m',e.target.value)} className="text-sm h-8" /></td><td className="p-1"><Input value={familyDetails.income_expense[k+'_y']||''} onChange={e=>updateIE(k+'_y',e.target.value)} className="text-sm h-8" /></td></tr>
                        ))}
                        <tr className="bg-emerald-50 font-semibold"><td className="p-2">Total Income</td><td className="p-1"><Input value={familyDetails.income_expense.total_income_monthly||''} onChange={e=>updateIE('total_income_monthly',e.target.value)} className="text-sm h-8 font-semibold" /></td><td className="p-1"><Input value={familyDetails.income_expense.total_income_yearly||''} onChange={e=>updateIE('total_income_yearly',e.target.value)} className="text-sm h-8 font-semibold" /></td></tr>
                        <tr><td colSpan="3" className="p-2 font-semibold bg-red-50">EXPENSES</td></tr>
                        {[['Food', 'exp_food'], ['Housing', 'exp_housing'], ['Health', 'exp_health'], ['Transport', 'exp_transport'], ['Education', 'exp_education'], ['Deeni', 'exp_deeni'], ['Essentials', 'exp_essentials'], ['Non-essentials', 'exp_nonessentials'], ['Others', 'exp_others']].map(([l, k]) => (
                          <tr key={k} className="border-b border-slate-100"><td className="p-2 text-slate-600">{l}</td><td className="p-1"><Input value={familyDetails.income_expense[k+'_m']||''} onChange={e=>updateIE(k+'_m',e.target.value)} className="text-sm h-8" /></td><td className="p-1"><Input value={familyDetails.income_expense[k+'_y']||''} onChange={e=>updateIE(k+'_y',e.target.value)} className="text-sm h-8" /></td></tr>
                        ))}
                        <tr className="bg-red-50 font-semibold"><td className="p-2">Total Expenses</td><td className="p-1"><Input value={familyDetails.income_expense.total_expense_monthly||''} onChange={e=>updateIE('total_expense_monthly',e.target.value)} className="text-sm h-8 font-semibold" /></td><td className="p-1"><Input value={familyDetails.income_expense.total_expense_yearly||''} onChange={e=>updateIE('total_expense_yearly',e.target.value)} className="text-sm h-8 font-semibold" /></td></tr>
                        <tr className="bg-amber-50 font-semibold"><td className="p-2">Surplus / Deficit</td><td className="p-1"><Input value={familyDetails.income_expense.surplus_monthly||''} onChange={e=>updateIE('surplus_monthly',e.target.value)} className="text-sm h-8" /></td><td className="p-1"><Input value={familyDetails.income_expense.surplus_yearly||''} onChange={e=>updateIE('surplus_yearly',e.target.value)} className="text-sm h-8" /></td></tr>
                        <tr className="border-b"><td className="p-2 text-slate-600">Borrowing / Qardan</td><td className="p-1"><Input value={familyDetails.income_expense.borrowing_m||''} onChange={e=>updateIE('borrowing_m',e.target.value)} className="text-sm h-8" /></td><td className="p-1"><Input value={familyDetails.income_expense.borrowing_y||''} onChange={e=>updateIE('borrowing_y',e.target.value)} className="text-sm h-8" /></td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Assets and Liabilities</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-blue-700">ASSETS</h4>
                      {[['Residential', 'asset_residential'], ['Shop/Godown/Land', 'asset_shop'], ['Machinery/Vehicle', 'asset_machinery'], ['Stock/Raw material', 'asset_stock'], ['Goods on credit', 'asset_goods_credit'], ['Others', 'asset_others']].map(([l, k]) => <FieldRow key={k} label={l} value={familyDetails.assets_liabilities[k]} onChange={v=>updateAL(k,v)} />)}
                    </div>
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-red-700">LIABILITIES</h4>
                      {[['Borrowing/Qardan', 'liability_borrowing'], ['Goods on credit', 'liability_goods_credit'], ['Others', 'liability_others']].map(([l, k]) => <FieldRow key={k} label={l} value={familyDetails.assets_liabilities[k]} onChange={v=>updateAL(k,v)} />)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {currentStep === 3 && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldRow label="Education" value={assessment.education} onChange={v=>updateAssess('education',v)} />
                  <FieldRow label="Past Work Experience" value={assessment.past_experience} onChange={v=>updateAssess('past_experience',v)} />
                  <FieldRow label="Present Business" value={assessment.present_business} onChange={v=>updateAssess('present_business',v)} />
                  <FieldRow label="Family Business" value={assessment.family_business} onChange={v=>updateAssess('family_business',v)} />
                  <div className="md:col-span-2"><FieldRow label="Skills and Knowledge" value={assessment.skills} onChange={v=>updateAssess('skills',v)} type="textarea" /></div>
                </div>
                <FieldRow label="Counsellor's Assessment" value={assessment.counsellor_assessment} onChange={v=>updateAssess('counsellor_assessment',v)} type="textarea" />
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-800">Products / Services</h3>
                    <Button variant="outline" size="sm" onClick={addProduct}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                  </div>
                  {assessment.products.map((pr, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end mb-2 p-3 bg-slate-50 rounded-lg">
                      <div className="col-span-5"><Label className="text-xs">Product/Service</Label><Input value={pr.name} onChange={e=>updateProduct(i,'name',e.target.value)} className="text-sm" /></div>
                      <div className="col-span-3"><Label className="text-xs">Unit</Label><Input value={pr.unit} onChange={e=>updateProduct(i,'unit',e.target.value)} className="text-sm" /></div>
                      <div className="col-span-3"><Label className="text-xs">Cost Price</Label><Input value={pr.cost_price} onChange={e=>updateProduct(i,'cost_price',e.target.value)} className="text-sm" /></div>
                      <div className="col-span-1 flex justify-end">{assessment.products.length > 1 && <Button variant="ghost" size="sm" onClick={()=>removeProduct(i)} className="text-red-500 h-9 w-9 p-0"><Trash2 className="w-4 h-4" /></Button>}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[['Trade Mark', 'trademark'], ['Online Presence', 'online_presence'], ['Digital Marketing', 'digital_marketing'], ['Store Location', 'store_location']].map(([l,k]) => <FieldRow key={k} label={l} value={assessment[k]} onChange={v=>updateAssess(k,v)} />)}
                  <div className="md:col-span-2"><FieldRow label="Supplier & Credit Details" value={assessment.supplier_details} onChange={v=>updateAssess('supplier_details',v)} type="textarea" /></div>
                  <div className="md:col-span-2"><FieldRow label="Customers & Competitors" value={assessment.customer_details} onChange={v=>updateAssess('customer_details',v)} type="textarea" /></div>
                  <div className="md:col-span-2"><FieldRow label="Major Expenses" value={assessment.major_expenses} onChange={v=>updateAssess('major_expenses',v)} type="textarea" /></div>
                </div>
                <FieldRow label="Business Assessment" value={assessment.business_assessment} onChange={v=>updateAssess('business_assessment',v)} type="textarea" />
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Support Areas Needed</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[['industry_product_knowledge','Industry/Product Knowledge'],['sourcing_support','Sourcing Support'],['sales_market_access','Sales/Market Access'],['internship_skill_development','Internship/Skill Development'],['mentoring_and_handholding','Mentoring & Handholding'],['book_keeping','Book keeping']].map(([k,l])=>(
                      <div key={k} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50"><Checkbox id={`s-${k}`} checked={!!assessment.support_areas[k]} onCheckedChange={()=>toggleSupport(k)} /><Label htmlFor={`s-${k}`} className="text-sm cursor-pointer">{l}</Label></div>
                    ))}
                  </div>
                  <div className="mt-3"><FieldRow label="Others" value={assessment.support_areas.others_text||''} onChange={v=>setAssessment(p=>({...p,support_areas:{...p.support_areas,others_text:v}}))} /></div>
                </div>
              </div>
            )}

            {/* STEP 4 */}
            {currentStep === 4 && (
              <div className="space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-800">Financial Assistance Required</h3>
                    <Button variant="outline" size="sm" onClick={addFA}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                  </div>
                  {businessPlan.financial_assistance.map((fa, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end mb-2 p-3 bg-slate-50 rounded-lg">
                      <div className="col-span-3"><Label className="text-xs">Timeline</Label><Input value={fa.timeline} onChange={e=>updateFA(i,'timeline',e.target.value)} className="text-sm" /></div>
                      <div className="col-span-3"><Label className="text-xs">Purpose</Label><Input value={fa.purpose} onChange={e=>updateFA(i,'purpose',e.target.value)} className="text-sm" /></div>
                      <div className="col-span-2"><Label className="text-xs">Amount</Label><Input value={fa.amount} onChange={e=>updateFA(i,'amount',e.target.value)} className="text-sm" /></div>
                      <div className="col-span-3"><Label className="text-xs">Support Doc</Label><Input value={fa.support_doc} onChange={e=>updateFA(i,'support_doc',e.target.value)} className="text-sm" /></div>
                      <div className="col-span-1 flex justify-end">{businessPlan.financial_assistance.length > 1 && <Button variant="ghost" size="sm" onClick={()=>removeFA(i)} className="text-red-500 h-9 w-9 p-0"><Trash2 className="w-4 h-4" /></Button>}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldRow label="Self-funding Available" value={businessPlan.self_funding} onChange={v=>updateBP('self_funding',v)} />
                  <FieldRow label="Rahen Available" value={businessPlan.rahen_available} onChange={v=>updateBP('rahen_available',v)} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Repayment Schedule</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-100"><th className="p-2 text-left w-24"></th>{['Year 1','Year 2','Year 3','Year 4','Year 5'].map(yr=><th key={yr} className="p-2 text-left">{yr}</th>)}</tr></thead>
                      <tbody>
                        {[['QH 1','qh1'],['QH 2','qh2'],['NA','na'],['TOTAL','total']].map(([l,p])=>(
                          <tr key={p} className={p==='total'?'bg-blue-50 font-semibold':'border-b'}><td className="p-2">{l}</td>
                            {[1,2,3,4,5].map(yr=><td key={yr} className="p-1"><Input value={businessPlan.repayment_schedule[`${p}_y${yr}`]||''} onChange={e=>updateRepay(`${p}_y${yr}`,e.target.value)} className="text-sm h-8" /></td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Targeted Economic Growth</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-100"><th className="p-2 text-left w-40">Category</th>{['Present','Year 1','Year 2','Year 3','Year 4','Year 5'].map(yr=><th key={yr} className="p-2 text-left">{yr}</th>)}</tr></thead>
                      <tbody>
                        {[['REVENUE','revenue',true],['Raw material','exp_raw_material',false],['Labor','exp_labor',false],['Rent','exp_rent',false],['Overhead','exp_overhead',false],['Maintenance','exp_maintenance',false],['Total Expenses','total_expenses',true],['PROFIT','profit',true],['(-) Credit/Dead stock','credit_deadstock',false],['(-) Qardan Repay','qardan_repayment',false],['(-) Household','household_expense',false],['CASH SURPLUS','cash_surplus',true]].map(([l,k,b])=>(
                          <tr key={k} className={b?'bg-blue-50 font-semibold':'border-b'}><td className="p-2">{l}</td>
                            {['present','year1','year2','year3','year4','year5'].map(yr=><td key={yr} className="p-1"><Input value={businessPlan.economic_growth[`${k}_${yr}`]||''} onChange={e=>updateGrowth(`${k}_${yr}`,e.target.value)} className="text-sm h-8" /></td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 5 */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800">
                  The applicant hereby confirms that the information provided is correct. Any amount granted will be utilized only for the business purpose as mentioned.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldRow label="Applicant Name" value={declaration.applicant_name || personalDetails.name} onChange={v=>updateDecl('applicant_name',v)} />
                  <FieldRow label="Counsellor Name" value={declaration.counsellor_name} onChange={v=>updateDecl('counsellor_name',v)} />
                  <FieldRow label="TR Committee Member" value={declaration.tr_member_name} onChange={v=>updateDecl('tr_member_name',v)} />
                </div>
                <FieldRow label="Comments" value={declaration.comments} onChange={v=>updateDecl('comments',v)} type="textarea" />

                <Button className="w-full h-12 text-base" onClick={handleSubmit} disabled={submitting} data-testid="submit-btn">
                  {submitting ? (
                    <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Submitting...</>
                  ) : (
                    <><Send className="w-5 h-5 mr-2" />Submit Application</>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setCurrentStep(s => Math.max(s - 1, 1))} disabled={currentStep === 1}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          {currentStep < 5 && (
            <Button onClick={() => setCurrentStep(s => Math.min(s + 1, 5))}>
              Next <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
